import { DateTime } from 'luxon';

import { translate } from './translate';

// Interface for DateTimeFormat
export interface DateTimeFormat {
    isSameTimeZone: boolean;
    timezone: string;
    date: DateTime;
    isoDate: string;
    description: string;
    source: any;
    getType(): DateTime;
    parseJsDateTime(jsDate: Date): this;
    parseUnixTime(unixTime: number): this;
    parseDateTime(dateString?: string | number | null | undefined | DateTimeFormat, description?: string): this;
    formatUTCDateToLocal(): this;
    formatLocalDateToUTC(): this;
    formatDateTimeToString(dateFormat?: string): string;
    clone(): DateTimeFormat;
    now(): DateTimeFormat;
    startOf(of: string): this;
    copyTimeFrom(source: DateTimeFormat): this;
    endOf(of: string): this;
    addDay(days: number): this;
    plus(add: object): this;
    minus(remove: object): this;
    getDate(nativeDate: boolean): DateTime;
    getDateFromDays(
        dateType: string,
        days: number,
        today?: DateTimeFormat
    ): { startDate: DateTimeFormat | null; endDate: DateTimeFormat | null };
    getDiffInSeconds: (other: DateTimeFormat) => number;
    getDiff: (other: DateTimeFormat, unit: string) => number;
    timeAgo(other: DateTimeFormat): string;
    isAfter(date: DateTime): boolean;
    isBefore(date: DateTime): boolean;
    isEqual(date: DateTime): boolean;
    isSame(date: DateTime, unit?: 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | 'millisecond'): boolean;
    isBetween(startDate: DateTime, endDate: DateTime, inclusive?: boolean): boolean;
    toString(format?: string): string;
    toIso(withZ?: boolean): string;
    toJSDate(): Date;
    fromJSDate(date: Date): DateTimeFormat;
    fromLuxon(date: DateTime): DateTimeFormat;
    toTimestamp(): number;
    roundToNearest30Minutes(): this;
    roundToNearest5Minutes(): this;
    setHoursAndMinutes(hours: number, minutes: number, keepLocalTime?: boolean): this;
}

// dateTimeFormat class implements the DateTimeFormat interface
export class dateTimeFormat implements DateTimeFormat {
    public timezone: string;
    public isSameTimeZone: boolean;
    public date: DateTime;
    public isoDate: string;
    public description: string;
    public source: any;

    constructor(timezone = 'UTC') {
        this.timezone = timezone;
        this.isSameTimeZone = this.checkTimeZone();
        this.date = DateTime.now(); // Initialize with current date
    }

    getType = (): DateTime => {
        return DateTime;
    };

    roundToNearest30Minutes = (): this => {
        const minutes = this.date.minute;
        const roundedMinutes = Math.round(minutes / 30) * 30;

        // Update the date, adjusting the hours if necessary
        if (roundedMinutes === 60) {
            this.date = this.date.plus({ hours: 1 }).set({ minute: 0 }).set({ second: 0 });
        } else {
            this.date = this.date.set({ minute: roundedMinutes }).set({ second: 0 });
        }

        return this;
    };

    roundToNearest5Minutes = (): this => {
        const minutes = this.date.minute;
        const roundedMinutes = Math.round(minutes / 5) * 5;

        // Update the date, adjusting the hours if necessary
        if (roundedMinutes === 60) {
            this.date = this.date.plus({ hours: 1 }).set({ minute: 0 }).set({ second: 0 });
        } else {
            this.date = this.date.set({ minute: roundedMinutes }).set({ second: 0 });
        }

        return this;
    };

    setHoursAndMinutes = (hours: number, minutes: number, keepLocalTime = false): this => {
        this.date = this.date
            .setZone(this.timezone, { keepLocalTime })
            .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
            .setZone('UTC');
        return this; // Return the instance for chaining
    };

    timeAgo = (other: DateTimeFormat): string => {
        const diffInSeconds = Math.abs(this.getDiffInSeconds(other));

        const units: { [key: string]: number } = {
            year: 31536000,
            month: 2592000,
            week: 604800,
            day: 86400,
            hour: 3600,
            minute: 60,
            second: 1,
        };

        for (const unit in units) {
            const value = units[unit];
            const elapsed = Math.floor(diffInSeconds / value);
            if (elapsed > 0) {
                return `${elapsed} ${translate(`${unit}${elapsed > 1 ? 's' : ''}`)} ${translate('ago')}`;
            }
        }

        return translate('just now');
    };

    toJSDate(): Date {
        return this.date.toJSDate();
    }

    copyTimeFrom = (source: DateTimeFormat): this => {
        const sourceDate = source.date.toUTC();
        this.date = this.date.set({
            hour: sourceDate.hour,
            minute: sourceDate.minute,
            second: sourceDate.second,
            millisecond: sourceDate.millisecond,
        });
        return this; // Return `this` for chaining
    };

    fromJSDate = (jsDate: Date): DateTimeFormat => {
        const luxonDate = this.isSameTimeZone
            ? DateTime.fromJSDate(jsDate)
            : DateTime.fromJSDate(jsDate).setZone(this.timezone);
        return new dateTimeFormat(luxonDate);
    };

    fromLuxon = (dateTime: DateTime): DateTimeFormat => {
        const dtf = new dateTimeFormat(this.timezone);
        if (dateTime.offset === 0) {
            dtf.date = dateTime;
        } else {
            dtf.date = dateTime.setZone('UTC', { keepLocalTime: true });
        }

        return dtf;
    };

    //@ts-ignore
    parseJsDateTime = (jsDate: Date): DateTimeFormat => {
        const newInstance = new dateTimeFormat(this.timezone);

        // Adjust the date by the timezone offset
        newInstance.date = DateTime.fromJSDate(jsDate, { zone: 'utc' });

        return newInstance;
    };

    //@ts-ignore
    parseUnixTime = (unixTime: number): DateTimeFormat => {
        const newInstance = new dateTimeFormat(this.timezone);

        // Adjust the date by the timezone offset
        newInstance.date = DateTime.fromMillis(unixTime, { zone: this.timezone });

        return newInstance;
    };

    //@ts-ignore
    parseDateTime = (
        date: string | number | null | undefined | DateTimeFormat | DateTime,
        description?: string
    ): DateTimeFormat => {
        //@ts-ignore
        const newInstance = new dateTimeFormat(this.timezone); // Create a new instance with the same timezone
        if (!date) {
            // Get the current date in the timezone, then convert it to UTC
            newInstance.date = this.isSameTimeZone
                ? DateTime.now().toUTC()
                : DateTime.now().setZone(this.timezone).toUTC();
        } else if (date instanceof DateTime) {
            //@ts-ignore
            newInstance.date = DateTime.fromJSDate(date.toJSDate());
        } else if (date instanceof this.constructor) {
            //@ts-ignore
            Object.assign(newInstance.date, date.date);
        } else if (typeof date === 'number') {
            newInstance.date = DateTime.fromMillis(date).toUTC();
        } else {
            // If a date string is provided, format it to be in ISO 8601 format
            //@ts-ignore
            const isoString = date.replace('T', ' '); // Replace space with 'T' for ISO format
            //@ts-ignore
            const sanitizedString = isoString
                .replace('.000Z', '')
                .replace('.999Z', '')
                .replace(' Z', '')
                .replace('Z', '');

            // Parse the date string with the timezone and convert it to UTC
            newInstance.date = DateTime.fromFormat(
                sanitizedString,
                sanitizedString.includes('.') ? 'yyyy-MM-dd HH:mm:ss.SSS' : 'yyyy-MM-dd HH:mm:ss',
                { zone: 'GMT' }
            ).toUTC();
        }

        // Handle optional description if provided
        if (description) {
            newInstance.description = description;
        }

        // Update the ISO date and source with the current date values
        newInstance.isoDate = newInstance.date.toISO();
        newInstance.source = date; // Set the original date string as the source
        return newInstance; // Return the new instance
    };

    checkTimeZone = (): boolean => {
        const systemZone = DateTime.local().zoneName; // user's current zone
        return systemZone !== this.timezone;
    };

    now = (): DateTimeFormat => {
        const newInstance = new dateTimeFormat(this.timezone); // new instance

        const dt = this.isSameTimeZone ? DateTime.now() : DateTime.now().setZone(this.timezone);

        newInstance.date = dt.toUTC(); // convert to UTC after zone handling

        return newInstance;
    };

    clone = (): DateTimeFormat => {
        const clonedInstance = new dateTimeFormat(this.timezone);
        clonedInstance.date = this.date;
        return clonedInstance; // Return the new instance
    };

    formatUTCDateToLocal = (): this => {
        this.date = this.date.setZone(this.timezone, { keepLocalTime: true });
        return this;
    };

    formatLocalDateToUTC = (): this => {
        this.date = this.date.setZone('UTC', { keepLocalTime: true });
        return this;
    };

    toString = (format = 'yyyy-MM-dd HH:mm:ss'): string => {
        return `${this.date.toFormat(format)}Z`;
    };

    toIso = (withZ = false): string => {
        return this.date.toFormat(withZ ? "yyyy-MM-dd'T'HH:mm:ss'Z'" : "yyyy-MM-dd'T'HH:mm:ss");
    };

    cloneDateTime = () => {
        return DateTime.fromJSDate(this.date.toJSDate());
    };

    formatDateTimeToString = (dateFormat = 'dd MMM yyyy'): string => {
        const shouldIncludeOffset = dateFormat.includes('Z');
        const formattedDate = this.cloneDateTime()
            .setZone(this.timezone)
            .toFormat(dateFormat.replace('Z', '') || 'dd MMM yyyy HH:mm:ss');

        // Calculate the offset in hours and minutes
        const offsetMinutes = this.cloneDateTime().setZone(this.timezone).offset;
        const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
        const offsetSign = offsetMinutes >= 0 ? '+' : '-';

        const offset = shouldIncludeOffset ? `(${offsetSign}${String(offsetHours).padStart(2, '0')})` : '';

        if (dateFormat === 'Z') {
            return `${offset}`;
        }
        return `${formattedDate}${offset}`;
    };

    startOf = (of: string): this => {
        this.date = this.date.setZone(this.timezone).startOf(of).toUTC(); // Set time to the start of the day
        return this;
    };

    endOf = (of: string): this => {
        this.date = this.date.setZone(this.timezone).endOf(of).toUTC(); // Set time to the start of the day
        return this;
    };

    // Check if this date is after another date based on an optional unit
    isAfter = (date: DateTime): boolean => {
        return this.date.toMillis() > date.toMillis();
    };

    // Check if this date is before another date based on an optional unit
    isBefore = (date: DateTime): boolean => {
        return this.date.toMillis() <= date.toMillis();
    };

    // Check if this date is before another date based on an optional unit
    isEqual = (date: DateTime): boolean => {
        return this.date.toMillis() === date.toMillis();
    };

    // Check if this date is the same as another date based on an optional unit
    isSame = (
        date: DateTime,
        unit: 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | 'millisecond' = 'millisecond'
    ): boolean => {
        return this.date.hasSame(date, unit);
    };

    isBetween = (startDate: DateTime, endDate: DateTime, inclusive = false): boolean => {
        // Check if this date is between two dates (exclusive by default)
        if (inclusive) {
            return this.date.toMillis() >= startDate.toMillis() && this.date.toMillis() <= endDate.toMillis();
        } else {
            return this.date.toMillis() > startDate.toMillis() && this.date.toMillis() < endDate.toMillis();
        }
    };
    // New method to add days
    addDay = (days: number): this => {
        this.date = this.date.plus({ days }); // Add the number of days using Luxon's plus method
        return this; // Return the instance for chaining
    };

    plus = (add: object): this => {
        this.date = this.date.plus(add); // Add the number of days using Luxon's plus method
        return this; // Return the instance for chaining
    };

    minus = (remove: object): this => {
        this.date = this.date.minus(remove); // Add the number of days using Luxon's minus method
        return this; // Return the instance for chaining
    };
    getDate = (nativeDate = false): DateTime => {
        if (!nativeDate) {
            return this.date;
        }
        return DateTime.fromISO(this.date.toUTC()); // Native JS Date
    };
    // Get Date based on the provided dateType and days
    getDateFromDays = (
        dateType: string,
        days: number,
        today?: DateTimeFormat
    ): { startDate: DateTimeFormat | null; endDate: DateTimeFormat | null } => {
        let startDate: DateTime | null = null;
        let endDate: DateTime | null = null;
        if (!today) {
            today = new dateTimeFormat(this.timezone);
        }
        switch (dateType) {
            case 'activeDays':
                if (days === 0) {
                    startDate = today.clone().startOf('day');
                    endDate = today.clone().endOf('day');
                } else {
                    startDate = today.clone().startOf('day').minus({ days });
                    endDate = today.clone().endOf('day').minus({ days });
                }
                break;
            case 'lastDays':
                startDate = today
                    .clone()
                    .minus({ days: days - 1 })
                    .startOf('day');
                endDate = today.clone().endOf('day');
                break;
            case 'activeToday':
            case 'today':
                startDate = today.clone().startOf('day');
                endDate = today.clone().endOf('day');
                break;
            case 'yesterday':
                startDate = today.clone().minus({ days: 1 }).startOf('day');
                endDate = today.clone().minus({ days: 1 }).endOf('day');
                break;
            case 'thisWeek':
                startDate = today.clone().formatLocalDateToUTC().startOf('week').minus({ days: 1 }).startOf('day');
                endDate = today.clone();
                break;
            case 'lastWeek':
                startDate = today
                    .clone()
                    .formatLocalDateToUTC()
                    .minus({ weeks: 1 })

                    .startOf('week')
                    .minus({ days: 1 })
                    .startOf('day');
                endDate = today
                    .clone()
                    .formatLocalDateToUTC()
                    .minus({ weeks: 1 })
                    .endOf('week')
                    .minus({ days: 1 })
                    .endOf('day');
                break;
            case 'thisMonth':
                startDate = today.clone().formatLocalDateToUTC().startOf('month');
                endDate = today.clone();
                break;
            case 'lastMonth':
                startDate = today.clone().formatLocalDateToUTC().minus({ months: 1 }).startOf('month');
                endDate = today.clone().formatLocalDateToUTC().minus({ months: 1 }).endOf('month');
                break;
            case 'thisYear':
                startDate = today.clone().startOf('year');
                endDate = today.clone();
                break;
            case 'lastYear':
                startDate = today.clone().minus({ years: 1 }).startOf('year');
                endDate = today.clone().minus({ years: 1 }).endOf('year');
                break;
            default:
                throw new Error(`Invalid dateType: ${dateType}`);
        }

        return {
            startDate,
            endDate,
        };
    };
    // Get the difference in seconds between this date and another DateTimeFormat instance
    getDiffInSeconds = (other: DateTimeFormat): number => {
        return this.date.diff(other.date, 'seconds').seconds; // Calculate the difference in seconds
    };

    getDiff = (other: DateTimeFormat, unit: string): number => {
        const diff = this.date.diff(other.date, unit);
        return diff[unit]; // Calculate the difference in days
    };

    toTimestamp = (): number => {
        return this.date.toMillis();
    };
}
